import { Kind } from "graphql";
import { isRootQueryIncludeVariable } from "./rootQueryInclude";
import { findEntryPointQuery } from "../documentUtils/entrypoint";
import { extractFragmentNamesFromField, extractVariableNamesFromTopLevelField } from "../documentUtils/field";
import { getFragmentDefinitions } from "../documentUtils/fragment";
import { createQueryDocument } from "../documentUtils/query";
export const UNKNOWN_SCHEMA = 'UNKNOWN_SCHEMA';
function getFieldsBySchemaInitState(schemaIdToApolloLink) {
    return Object.entries(schemaIdToApolloLink).reduce((acc, [schemaId, schemaLink])=>{
        acc[schemaId] = {
            fields: [],
            fragmentNodes: [],
            link: schemaLink,
            containsNonNullableFields: false
        };
        return acc;
    }, {});
}
export function splitQueryBySchema(document, topLevelFieldNameToMetadata, schemaIdToApolloLink, newQueryName) {
    const fieldsBySchema = getFieldsBySchemaInitState(schemaIdToApolloLink);
    const fragmentsInRootOperation = new Map(getFragmentDefinitions(document).map((fragment)=>[
            fragment.name.value,
            fragment
        ]));
    const queryDefinition = findEntryPointQuery(document);
    queryDefinition.selectionSet.selections.forEach((selectionNode)=>{
        if (selectionNode.kind !== Kind.FIELD) {
            return;
        }
        // [2.2] Check schema mapping for top level field
        const topLevelFieldName = selectionNode.name.value;
        const topLevelFieldData = topLevelFieldNameToMetadata[topLevelFieldName];
        // [2.3] Create or add field to the list in the schema key
        if (typeof (topLevelFieldData === null || topLevelFieldData === void 0 ? void 0 : topLevelFieldData.schemaId) === 'undefined') {
            // [2.3.1] Add fields without a schema to a special unknown schema for error handling
            fieldsBySchema[UNKNOWN_SCHEMA].fields.push(selectionNode);
        } else {
            const fieldsBySchemaData = fieldsBySchema[topLevelFieldData.schemaId];
            fieldsBySchemaData.fields.push(selectionNode);
            // [2.3.4] Determine whether sub-query contains non-nullable fields
            if (fieldsBySchemaData.containsNonNullableFields === false) {
                fieldsBySchemaData.containsNonNullableFields = topLevelFieldData.nonNullable;
            }
        }
    });
    // [3] Gather all variable names and fragment names from fields by schema and pull them from operation
    Object.entries(fieldsBySchema).forEach(([schemaKey, { fields }])=>{
        const groupedVariableNamesForFields = Array.from(new Set(fields.flatMap(extractVariableNamesFromTopLevelField)));
        const variableDefinitionsForAllFields = getVariableDefinitionNodesFromOperationDefinition(queryDefinition, groupedVariableNamesForFields);
        fieldsBySchema[schemaKey].variables = variableDefinitionsForAllFields;
        // Extract fragment used in this schema's fields
        const usedFragmentNames = fields.flatMap((field)=>extractFragmentNamesFromField(field));
        const usedFragmentNamesSet = new Set(usedFragmentNames);
        const usedFragmentNodes = Array.from(usedFragmentNamesSet).map((name)=>fragmentsInRootOperation.get(name)).filter((fragmentNode)=>typeof fragmentNode !== 'undefined');
        fieldsBySchema[schemaKey].fragmentNodes = usedFragmentNodes;
    });
    // [4] Build split queries
    const queriesBySchema = [];
    Object.entries(fieldsBySchema).forEach(([schemaKey, queryBySchemaData])=>{
        // [4.1] Skip creating queries for schemas with no fields in the composition query
        if (queryBySchemaData.fields.length > 0) {
            var _queryBySchemaData_variables;
            const includeVariableNames = ((_queryBySchemaData_variables = queryBySchemaData.variables) === null || _queryBySchemaData_variables === void 0 ? void 0 : _queryBySchemaData_variables.filter((variable)=>isRootQueryIncludeVariable(variable.variable)).map((variable)=>variable.variable.name.value)) || [];
            queriesBySchema.push({
                queryDoc: constructSchemaSubQueryDoc(newQueryName, schemaKey, queryBySchemaData),
                schemaLink: queryBySchemaData.link,
                containsNonNullableFields: queryBySchemaData.containsNonNullableFields,
                includeVariableNames
            });
        }
    });
    return queriesBySchema;
}
function schemaKeyToCamelCase(schemaKey) {
    return schemaKey.charAt(0).toUpperCase() + schemaKey.slice(1);
}
function constructSchemaSubQueryDoc(newQueryName, schemaKey, queryBySchemaData) {
    const splitQueryName = `${newQueryName}${schemaKeyToCamelCase(schemaKey)}SplitQuery`;
    return createQueryDocument(splitQueryName, queryBySchemaData.variables || [], queryBySchemaData.fields, queryBySchemaData.fragmentNodes);
}
function getVariableDefinitionNodesFromOperationDefinition(operation, variableNames) {
    if (typeof operation.variableDefinitions !== 'undefined') {
        return operation.variableDefinitions.filter((variableDefinition)=>variableNames.includes(variableDefinition.variable.name.value));
    }
    return [];
}

//# sourceMappingURL=splitQueryBySchema.js.map